/**
 * The customer domain, as the portal sees it.
 *
 * The license shapes are NOT declared here. They come from
 * `shared/customer_contract.ts`, the verbatim mirror of
 * `aloftwx-api/worker/customer_contract.ts`, because aloftwx-api owns the
 * licensing database and therefore owns the vocabulary. This file re-exports
 * them under the names the SPA imports, and adds the things that are genuinely
 * the portal's own: its session shape, its `/api/*` wire types, and the limits
 * this product enforces.
 *
 * This directory is compiled by BOTH `tsconfig.app.json` (the SPA) and
 * `tsconfig.worker.json` (the Worker), because most `/api/*` responses are
 * these types verbatim. Keep it to types and pure constants: no DOM globals, no
 * Workers globals, no `postgres` import.
 *
 * Timestamps are ISO-8601 STRINGS everywhere. `src/lib/format.ts` is the only
 * place that parses one.
 */

import type { CustomerLicense } from "./customer_contract";

export type {
  ClaimLicenseInput,
  ClaimOutcome,
  CustomerLicense,
  CustomerLicenseDetail,
  CustomerLicenseList,
  CustomerPingResult,
  CustomerRevealedKey,
  CustomerSeat,
  LicenseSource,
  LicenseStatus,
  SeatReleaseResult,
} from "./customer_contract";

// ─── the session ─────────────────────────────────────────────────────────────

/**
 * Who the browser is, as `GET /api/session` reports it.
 *
 * Unlike the console's `/bff/me` there is a signed-out answer, and it is a 200
 * rather than a 401: "nobody is signed in" is the normal state of a public
 * page, and making the SPA's very first request an error would mean every
 * signed-out visitor sees an error boundary flash before the sign-in form.
 */
export type SessionState =
  | { signed_in: false }
  | {
      signed_in: true;
      email: string;
      /** ISO-8601. The portal warns rather than silently dropping the session. */
      expires_at: string;
      /** ISO-8601. When this account first signed in. */
      member_since: string;
    };

// ─── /api wire shapes the portal owns ────────────────────────────────────────

/**
 * The answer to "send me a link", and it is deliberately the same object
 * whether or not the address is one we have ever seen.
 *
 * A sign-in form that says "no account with that email" is an account
 * enumeration oracle on a site whose accounts are purchase records. Since any
 * address may sign in — a customer who bought elsewhere signs in first and
 * claims their key second — there is no such thing as an unknown address here
 * anyway, which is what makes the safe answer also the honest one.
 */
export type LinkRequested = {
  /** Always true. The screen says "if that address can receive mail, check it". */
  sent: true;
  /**
   * Present ONLY when the Worker is running with `PORTAL_DEV_LINKS=1` in
   * `.dev.vars`, so local development does not need a mail provider. The
   * production Worker has no way to set it — see `worker/email.ts`.
   */
  dev_link?: string;
};

/** One browser session on the account, as `/api/session/devices` lists them. */
export type SessionRecord = {
  id: string;
  /** ISO-8601. */
  created_at: string;
  /** ISO-8601. */
  last_seen_at: string;
  /** Coarse, from the User-Agent: `Chrome on Windows`. Never the raw string. */
  agent: string;
  /** True for the session making the request — the one you should not revoke. */
  current: boolean;
};

/** The portal's own error envelope. Matches the console's `/bff` shape. */
export type ApiErrorBody = {
  error: { code: string; message: string };
};

// ─── products ────────────────────────────────────────────────────────────────

/**
 * Where a product's own pages live, keyed by the `product` slug the API
 * returns.
 *
 * Deliberately a portal-side table rather than a field on `CustomerLicense`:
 * these are marketing and download URLs that change on a website's schedule,
 * and putting them in the licensing database would mean a migration to fix a
 * typo in a link. An unknown slug renders with no links rather than breaking —
 * a second product must be able to appear here before this table knows it.
 */
export type ProductLinks = {
  /** The product's page on the marketing site. */
  home?: string;
  /**
   * Where the installer comes from. Never a dl.downwind.studio URL: the root
   * 404s, every byte URL names a version, and a product behind a licence is a
   * 401 to a browser. Point at a route that resolves the current release.
   */
  download?: string;
  /** Docs, install guide, or the closest thing to one. */
  docs?: string;
};

export const PRODUCT_LINKS: Readonly<Record<string, ProductLinks>> = {
  aloftwx: {
    home: "https://aloftwx.com",
    // 302s to the current Downwind Manager installer, which fetches AloftWX
    // once the key is in. The purchase email and Polar note use the same link.
    download: "https://aloftwx.com/api/download",
  },
};

/** A product panel needs an order; slugs the API has and this table does not go last. */
export function productOrder(slug: string): number {
  const known = Object.keys(PRODUCT_LINKS).indexOf(slug);
  return known === -1 ? Number.MAX_SAFE_INTEGER : known;
}

/** Group a flat license list into the panels the overview renders. */
export function byProduct(
  licenses: ReadonlyArray<CustomerLicense>,
): Array<{ product: string; product_name: string; licenses: CustomerLicense[] }> {
  const groups = new Map<string, { product_name: string; licenses: CustomerLicense[] }>();
  for (const license of licenses) {
    const group = groups.get(license.product);
    if (group) group.licenses.push(license);
    else groups.set(license.product, { product_name: license.product_name, licenses: [license] });
  }
  return [...groups.entries()]
    .map(([product, group]) => ({ product, ...group }))
    .sort((a, b) => productOrder(a.product) - productOrder(b.product));
}

// ─── the edition ─────────────────────────────────────────────────────────────

/**
 * How `CustomerLicense.edition` is written for the person who bought it.
 *
 * The value names a SIMULATOR — `msfs2024`, and one day `xplane12` — not a
 * tier, because there are no tiers to be at the bottom of. It arrives as an id
 * because it is one, and an id is not a thing to show a customer on the page
 * where they check what they paid for.
 *
 * Portal-side for the same reason `PRODUCT_LINKS` is: this is how a customer
 * screen words something, and the licensing database should not need a
 * migration to reword it. An unknown edition renders as itself, which is worse
 * than a name and much better than a blank.
 */
export const EDITION_LABELS: Readonly<Record<string, string>> = {
  msfs2024: "MSFS 2024",
};

export function editionName(edition: string): string {
  return EDITION_LABELS[edition] ?? edition;
}

// ─── limits, restated so a form can enforce them before the round trip ───────

/**
 * How long a sign-in link is good for.
 *
 * Fifteen minutes is the usual number and it is wrong for this product. These
 * links arrive in the inbox of somebody who just bought flight-sim software and
 * may well be mid-flight; twenty minutes of walking away is not an attack, it
 * is a Tuesday. An hour is still short against the theft model that matters
 * here (a shared or forwarded mailbox), and the link is single-use regardless.
 */
export const LINK_TTL_MINUTES = 60;

/** Sessions are long because the alternative is emailing a link every visit. */
export const SESSION_TTL_DAYS = 60;

/**
 * Sign-in links per address per hour.
 *
 * This limit protects the *recipient*, not us: an attacker who cannot read the
 * mailbox gains nothing from requesting links, but they can absolutely use the
 * form to bury a real person's inbox. Keyed by address for that reason, with a
 * separate per-IP ceiling so one host cannot spray many addresses.
 */
export const LINK_REQUESTS_PER_HOUR_PER_EMAIL = 5;
export const LINK_REQUESTS_PER_HOUR_PER_IP = 20;

/**
 * Key claims per account per hour.
 *
 * `claimLicense` tells the caller whether a key exists, which is the trade the
 * contract documents. This is the throttle that makes that trade safe, and it
 * lives here — keyed by the account doing the guessing — rather than in the API,
 * for the same reason the Discord bot throttles per Discord user: this is where
 * the identity being throttled actually exists.
 */
export const CLAIMS_PER_HOUR_PER_ACCOUNT = 10;

/** Matches `isPlausibleLicenseKey` in aloftwx-api/worker/hashing.ts. */
export const MIN_NORMALIZED_KEY_LENGTH = 16;
export const MAX_NORMALIZED_KEY_LENGTH = 64;

/**
 * Matches `KEY_PREFIX_LENGTH` in aloftwx-api/worker/hashing.ts.
 *
 * The most of a key that may ever be written down — it is an indexed,
 * non-secret column on `licenses` and it is what support quotes back at a
 * customer ("does yours start with ALOFTK7M?"). The portal uses it for the same
 * two jobs: identifying a license in a list without revealing it, and recording
 * a failed claim in `license_claims` without recording the guess.
 */
export const KEY_PREFIX_LENGTH = 8;

/**
 * Uppercase, non-alphanumerics stripped — the same normalization the API
 * applies before hashing. Restated here so the claim form can show the customer
 * what will actually be looked up, and so an obvious typo is caught before a
 * round trip that costs them a rate-limit slot.
 */
export function normalizeLicenseKey(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function isPlausibleLicenseKey(normalized: string): boolean {
  return (
    normalized.length >= MIN_NORMALIZED_KEY_LENGTH && normalized.length <= MAX_NORMALIZED_KEY_LENGTH
  );
}

/**
 * The loosest thing that is still an email address.
 *
 * Deliberately not a "real" RFC 5322 pattern. The only check that matters is
 * whether mail arrives, and that check is the link itself — a stricter regex
 * here buys nothing and reliably rejects somebody's perfectly valid address.
 */
export function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length >= 3 && trimmed.length <= 254 && /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(trimmed)
  );
}

/** Lowercased and trimmed — the form the portal stores and the API hashes. */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}
